
import { action, observable } from 'mobx';
import {
	Model,
	IModelAttributes,
	attribute,
	entity,
	ReferencePath,
} from 'Models/Model';
import * as Models from 'Models/Entities';
import * as Validators from 'Validators';
import { CRUD } from '../CRUDOptions';
import * as AttrUtils from 'Util/AttributeUtils';
import { IAcl } from 'Models/Security/IAcl';
import {
	makeFetchOneToManyFunc,
	getCreatedModifiedCrudOptions,
} from 'Util/EntityUtils';
import UserPpsrSearchCertificate from 'Models/Security/Acl/UserPpsrSearchCertificate';
import VisitorsPpsrSearchCertificate from 'Models/Security/Acl/VisitorsPpsrSearchCertificate';
import { EntityFormMode } from 'Views/Components/Helpers/Common';
import SuperAdministratorScheme from '../Security/Acl/SuperAdministratorScheme';


export interface IPpsrSearchCertificateEntityAttributes extends IModelAttributes {
	content: string;

	ppsrSearchId: string;
	ppsrSearch: Models.PpsrSearchEntity | Models.IPpsrSearchEntityAttributes;

}

@entity('PpsrSearchCertificateEntity', 'PPSR Search Certificate')
export default class PpsrSearchCertificateEntity extends Model
	implements IPpsrSearchCertificateEntityAttributes {
	public static acls: IAcl[] = [
		new SuperAdministratorScheme(),
		new UserPpsrSearchCertificate(),
		new VisitorsPpsrSearchCertificate(),

	];

	/**
	 * Fields to exclude from the JSON serialization in create operations.
	 */
	public static excludeFromCreate: string[] = [];

	/**
	 * Fields to exclude from the JSON serialization in update operations.
	 */
	public static excludeFromUpdate: string[] = [];

	@observable
	@attribute<PpsrSearchCertificateEntity, string>()
	@CRUD({
		name: 'Content',
		displayType: 'textarea',
		order: 10,
		headerColumn: true,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public content: string;
	

	@Validators.Required()
	@observable
	@attribute()
	@CRUD({
		
		name: 'PPSR Search',
		displayType: 'reference-combobox',
		order: 20,
		referenceTypeFunc: () => Models.PpsrSearchEntity,
		referenceResolveFunction: makeFetchOneToManyFunc({
			relationName: 'ppsrSearchs',
			oppositeEntity: () => Models.PpsrSearchEntity,
		}),
		
	})
	public ppsrSearchId: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public ppsrSearch: Models.PpsrSearchEntity;



	// eslint-disable-next-line @typescript-eslint/no-useless-constructor
	constructor(attributes?: Partial<IPpsrSearchCertificateEntityAttributes>) {
		super(attributes);
	}

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * Any reference objects that are passed in are converted to models if they are not already.
	 * This function is called from the constructor to assign the initial fields.
	 */
	@action
	public assignAttributes(attributes?: Partial<IPpsrSearchCertificateEntityAttributes>) {
		super.assignAttributes(attributes);

		if (attributes) {
			if (attributes.content !== undefined) {
				this.content = attributes.content;
			}
			if (attributes.ppsrSearchId !== undefined) {
				this.ppsrSearchId = attributes.ppsrSearchId;
			}
			if (attributes.ppsrSearch !== undefined) {
				if (attributes.ppsrSearch === null) {
					this.ppsrSearch = attributes.ppsrSearch;
				} else if (attributes.ppsrSearch instanceof Models.PpsrSearchEntity) {
					this.ppsrSearch = attributes.ppsrSearch;
					this.ppsrSearchId = attributes.ppsrSearch.id;
				} else {
					this.ppsrSearch = new Models.PpsrSearchEntity(attributes.ppsrSearch);
					this.ppsrSearchId = this.ppsrSearch.id;
				}
			}

		}
	}

	/**
	 * Additional fields that are added to GraphQL queries when using the
	 * the managed model APIs.
	 */
	public defaultExpands = `
		ppsrSearch {
			${Models.PpsrSearchEntity.getAttributes().join('\n')}
		}
	`;

	/**
	 * The save method that is called from the admin CRUD components.
	 */
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	public async saveFromCrud(formMode: EntityFormMode) {
		const relationPath: ReferencePath = {
		};
		return this.save(
			relationPath,
			{
				options: [
					{
						key: 'mergeReferences',
						graphQlType: '[String]',
						value: [
							'ppsrSearch',
						],
					},
				],
			},
		);
	}

	/**
	 * Returns the string representation of this entity to display on the UI.
	 */
	public getDisplayName() {
		return this.id;
	}
}

/*
 * Retrieve the created and modified CRUD attributes for defining the CRUD views and decorate the class with them.
 */
const [createdAttr, modifiedAttr] = getCreatedModifiedCrudOptions();
CRUD(createdAttr)(PpsrSearchCertificateEntity.prototype, 'created');
CRUD(modifiedAttr)(PpsrSearchCertificateEntity.prototype, 'modified');
